import React, { useEffect, useState } from "react";

import type { ClientId } from "../../domain/types";
import type { ZohoStepDTO } from "../../data/zohoConnectApi";
import { runPrepareZoho } from "../../data/zohoConnectApi";
import { useCoClient } from "../../data/store";
import { ConnectionSequence } from "../../components/ConnectionSequence";
import { SubStepShell } from "../../components/SubStepShell";
import { useSubStepNav } from "../useSubStepNav";

/**
 * Housekeeping in the client's Zoho Books before Zournal works in them.
 *
 * No start button: arriving here is the trigger, the mock's design kept. The
 * server re-reads every setting before touching it, so the run that fires on
 * each visit writes nothing once the books are right — and a setting somebody
 * flipped back inside Zoho gets caught and fixed on the next visit instead of
 * hiding behind a remembered tick.
 *
 * The rows come from the server ready-made (ConnectionSequence's rule), and a
 * refused setting arrives as a failed row with Zoho's reason, not as a broken
 * request. Continue opens when every setting is confirmed right, which is the
 * same gate the confirm-connection screen uses and for the same reason: the
 * point of running server-side is that "done" means checked, not attempted.
 */
export function PrepareZohoStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "prepare-zoho");

  const [steps, setSteps] = useState<ZohoStepDTO[] | undefined>();
  /**
   * Set when the run itself could not happen — no connected books, network
   * down. Different thing from a failed row: there are no rows to show.
   */
  const [refusal, setRefusal] = useState<string | null>(null);
  // Bumped by "Try again" to fire the effect once more.
  const [attempt, setAttempt] = useState(0);

  // Arriving is the trigger — which is exactly why a LIVE client must not
  // arrive. This is the one step whose mount reaches into the customer's Zoho
  // Books and writes; the read-only wrapper the wizard puts around a live
  // client disables its controls but cannot stop an effect, so the effect
  // stops itself. Everything else that runs on mount either reads or is
  // skip-if-unchanged.
  const locked = Boolean(client?.liveAt);

  useEffect(() => {
    if (locked) return;
    let live = true;

    setRefusal(null);
    runPrepareZoho(clientId)
      .then((state) => {
        if (live) setSteps(state.steps);
      })
      .catch((e: unknown) => {
        if (live) setRefusal(e instanceof Error ? e.message : "Could not reach the server.");
      });

    return () => {
      live = false;
    };
  }, [clientId, attempt, locked]);

  if (!client) return null;

  const name = client.name || "this client";

  const anyFailed = (steps ?? []).some((step) => step.status === "failed");
  const complete = steps !== undefined && steps.length > 0 && steps.every((step) => step.status === "done");

  const retry = () => {
    // Keep the rows on screen while the retry runs; only the outcome replaces
    // them. Clearing to the dashed box would make one click look like a reload.
    setAttempt((n) => n + 1);
  };

  return (
    <SubStepShell
      title="Setting Up Zoho"
      description="We add a few custom settings below to help Zoho Books work properly with Zournal."
      thenWhat={complete ? "Continue → Assign Your Team." : "Continue opens once the books are set up."}
      onContinue={() => void goNext()}
      continueDisabled={!complete}
    >
      {locked ? (
        <div className="rounded-lg border border-dashed px-5 py-4 text-[13px] text-muted-foreground">
          {name} is live — this housekeeping already ran, and re-running it would write to their books. Zoho
          settings are managed in {name}'s own Zoho Books from here on.
        </div>
      ) : steps ? (
        <ConnectionSequence
          title="Preparing Zoho for Zournal"
          steps={steps}
          onRetry={anyFailed ? retry : undefined}
          // Each row is a setting the CA may have to go and fix inside Zoho, so
          // the state is spelt out rather than left to a tick.
          showStatusWord
          // The detail under a tick is the outcome — "was auto-generating
          // INV-000001" — which on this screen is the entire point of the row.
          showDoneDetail
        />
      ) : refusal ? (
        <div className="flex flex-col items-start gap-2 rounded-lg border border-dashed px-5 py-4">
          <p className="text-sm text-muted-foreground">{refusal}</p>
          <button
            type="button"
            onClick={retry}
            className="rounded-md border bg-background px-3 py-[5px] text-[13px] font-medium transition-colors hover:bg-muted/40"
          >
            Try again
          </button>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed px-5 py-4 text-[13px] text-muted-foreground">
          Looking at {name}'s Zoho settings…
        </div>
      )}
    </SubStepShell>
  );
}
